/**
 * Mobile help content - gestures and feature tips
 * Shown instead of keyboard shortcuts on touch devices
 */

export interface GestureHint {
  id: string;
  gesture: string;
  description: string;
  icon: 'tap' | 'long-press' | 'pinch' | 'drag' | 'swipe' | 'two-finger';
}

export interface FeatureTip {
  id: string;
  title: string;
  description: string;
  icon: 'upload' | 'text' | 'layers' | 'export' | 'product' | 'undo' | 'zoom' | 'draw';
}

/**
 * Touch gesture hints for mobile users
 */
export const gestureHints: GestureHint[] = [
  {
    id: 'tap-select',
    gesture: 'Tap',
    description: 'Select a layer or tool',
    icon: 'tap',
  },
  {
    id: 'long-press',
    gesture: 'Long Press',
    description: 'Open context menu or edit text',
    icon: 'long-press',
  },
  {
    id: 'pinch-zoom',
    gesture: 'Pinch',
    description: 'Zoom in or out on the canvas',
    icon: 'pinch',
  },
  {
    id: 'two-finger-pan',
    gesture: 'Two-Finger Drag',
    description: 'Pan around the canvas',
    icon: 'two-finger',
  },
  {
    id: 'swipe-tabs',
    gesture: 'Swipe Left/Right',
    description: 'Switch between modes',
    icon: 'swipe',
  },
  {
    id: 'drag-move',
    gesture: 'Drag',
    description: 'Move selected layer',
    icon: 'drag',
  },
];

/**
 * Feature tips for mobile users
 */
export const featureTips: FeatureTip[] = [
  {
    id: 'tip-upload',
    title: 'Upload Image',
    description: 'Tap the Upload button to add your own images to the design',
    icon: 'upload',
  },
  {
    id: 'tip-text',
    title: 'Add Text',
    description: 'Use the Text tool to add custom text to your design',
    icon: 'text',
  },
  {
    id: 'tip-layers',
    title: 'Manage Layers',
    description: 'Switch to Layers tab to reorder, hide, or delete elements',
    icon: 'layers',
  },
  {
    id: 'tip-product',
    title: 'Switch Product',
    description: 'Tap the product name in the header to change products',
    icon: 'product',
  },
  {
    id: 'tip-undo',
    title: 'Undo Changes',
    description: 'Made a mistake? Tap the undo arrow to go back',
    icon: 'undo',
  },
  {
    id: 'tip-zoom',
    title: 'Zoom Controls',
    description: 'Use the zoom slider or pinch gesture to see details',
    icon: 'zoom',
  },
  {
    id: 'tip-draw',
    title: 'Draw Freely',
    description: 'Select Pen tool and draw directly on the canvas',
    icon: 'draw',
  },
  {
    id: 'tip-export',
    title: 'Export Design',
    description: 'When finished, tap Export PNG to save your design',
    icon: 'export',
  },
];

/**
 * Help panel tab types
 */
export type HelpTab = 'gestures' | 'tips';

/**
 * Tab labels for the help panel
 */
export const helpTabLabels: Record<HelpTab, string> = {
  gestures: 'Gestures',
  tips: 'Tips',
};
